import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

const DisputePolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <Link to="/">
          <Button variant="ghost" className="mb-6">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Button>
        </Link>

        <div className="bg-white rounded-lg shadow-sm p-8">
          <h1 className="text-3xl font-bold mb-2">Dispute Policy</h1>
          <p className="text-gray-600 mb-8">Effective Date: {new Date().toLocaleDateString()}</p>

          <div className="space-y-6 text-gray-700">
            <section>
              <h2 className="text-xl font-semibold mb-3 text-gray-900">1. What is a Dispute?</h2>
              <p>A dispute occurs when a customer contacts their bank or card issuer to challenge a charge on their account. This is also known as a chargeback.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3 text-gray-900">2. Before Filing a Dispute</h2>
              <p className="mb-2">We strongly encourage you to contact us BEFORE filing a dispute with your bank:</p>
              <ul className="list-disc ml-6 space-y-2">
                <li>Most issues can be resolved quickly through direct communication</li>
                <li>Disputes can take 60-90 days to resolve</li>
                <li>Contact us first for faster resolution</li>
                <li>Email: <a href="mailto:support@dresspay.com" className="text-indigo-600 hover:underline">support@dresspay.com</a></li>
                <li>Phone: <a href="tel:3464837337" className="text-indigo-600 hover:underline">346-483-7337</a></li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3 text-gray-900">3. Valid Reasons for Disputes</h2>
              <p className="mb-2">You may file a dispute if:</p>
              <ul className="list-disc ml-6 space-y-2">
                <li>You were charged but didn't receive the product</li>
                <li>The product was significantly different from the description</li>
                <li>You were charged an incorrect amount</li>
                <li>You were charged multiple times for the same order</li>
                <li>You were charged after canceling your order</li>
                <li>You suspect unauthorized or fraudulent charges</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3 text-gray-900">4. How We Handle Disputes</h2>
              <p className="mb-2">When a dispute is filed:</p>
              <ol className="list-decimal ml-6 space-y-2">
                <li>We receive notification from our payment processor (Stripe)</li>
                <li>We review your order history and transaction details</li>
                <li>We gather evidence including:
                  <ul className="list-disc ml-6 mt-2 space-y-1">
                    <li>Order confirmation and delivery records</li>
                    <li>Communication history with the customer</li>
                    <li>Product descriptions and images</li>
                    <li>Tracking information and proof of delivery</li>
                  </ul>
                </li>
                <li>We submit our response to the bank/card issuer</li>
                <li>The bank makes the final decision (typically within 60-90 days)</li>
              </ol>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3 text-gray-900">5. Dispute Evidence</h2>
              <p className="mb-2">To support your dispute claim, please provide:</p>
              <ul className="list-disc ml-6 space-y-2">
                <li>Your order confirmation code (5-digit number)</li>
                <li>Photos or videos showing the issue</li>
                <li>Detailed description of the problem</li>
                <li>Any communication with our customer service team</li>
                <li>Proof of non-delivery (if applicable)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3 text-gray-900">6. Fraudulent Disputes</h2>
              <p className="mb-2">Filing false or fraudulent disputes is illegal and may result in:</p>
              <ul className="list-disc ml-6 space-y-2">
                <li>Immediate account suspension</li>
                <li>Permanent ban from DressPay</li>
                <li>Legal action to recover costs and damages</li>
                <li>Reporting to relevant authorities</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3 text-gray-900">7. Dispute Timeline</h2>
              <ul className="list-disc ml-6 space-y-2">
                <li><strong>Day 1:</strong> Dispute filed by customer with bank</li>
                <li><strong>Day 1-3:</strong> We receive dispute notification</li>
                <li><strong>Day 3-10:</strong> We investigate and submit evidence</li>
                <li><strong>Day 10-90:</strong> Bank reviews and makes decision</li>
                <li><strong>Final Decision:</strong> Customer or merchant wins; decision is final</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3 text-gray-900">8. Dispute Outcomes</h2>
              <p className="mb-2">Possible outcomes:</p>
              <ul className="list-disc ml-6 space-y-2">
                <li><strong>Customer Wins:</strong> Charge is reversed; customer keeps product and gets refund</li>
                <li><strong>Merchant Wins:</strong> Charge stands; customer's bank returns funds to DressPay</li>
                <li><strong>Partial Resolution:</strong> Partial refund may be issued by bank</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3 text-gray-900">9. Alternative Resolution</h2>
              <p className="mb-2">Instead of filing a dispute, consider:</p>
              <ul className="list-disc ml-6 space-y-2">
                <li>Requesting a refund through our <Link to="/refund-policy" className="text-indigo-600 hover:underline">Refund Policy</Link></li>
                <li>Initiating a return through our <Link to="/return-policy" className="text-indigo-600 hover:underline">Return Policy</Link></li>
                <li>Contacting customer service for immediate assistance</li>
                <li>These methods are typically faster and more efficient</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3 text-gray-900">10. Contact Information</h2>
              <p className="mb-2">Before filing a dispute, please reach out:</p>
              <ul className="list-none ml-0 space-y-1">
                <li><strong>Email:</strong> <a href="mailto:support@dresspay.com" className="text-indigo-600 hover:underline">support@dresspay.com</a></li>
                <li><strong>Phone:</strong> <a href="tel:3464837337" className="text-indigo-600 hover:underline">346-483-7337</a></li>
                <li><strong>Hours:</strong> Monday - Friday, 9 AM - 6 PM EST</li>
                <li><strong>Response Time:</strong> Within 24 hours</li>
              </ul>
              <p className="mt-4 font-semibold">We're committed to resolving your concerns quickly and fairly!</p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DisputePolicy;